import {
  Box, Card, CardContent, Chip, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { api } from '@/api/client';
import { formatDateTime } from '@/utils/format';
import type { AIAssistanceEvent, AIHumanAction } from '@/types/medical';

/**
 * AI suggestions log.
 *
 * Every AI capability call attached to this clinician shows here, with
 * the human decision and timestamp. This IS the Pillar 10 receipt for the
 * clinician — it answers the question "what did I tell the model and what
 * did I decide about it?" without exposing patient PHI inside the table.
 */

const ACTIONS: { value: AIHumanAction | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Awaiting decision' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'edited', label: 'Edited & accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'escalated', label: 'Escalated' },
];

export function ClinicianAILogPage() {
  const q = useApiQuery({
    queryKey: ['clinician', 'ai-events'],
    queryFn:  () => api.get<AIAssistanceEvent[]>('/api/medical/clinician/ai-suggestions/'),
  });
  const [filter, setFilter] = useState<AIHumanAction | 'all'>('all');

  const filtered = useMemo(() => {
    const all = q.data ?? [];
    if (filter === 'all') return all;
    return all.filter((e) => e.human_action === filter);
  }, [q.data, filter]);

  return (
    <>
      <PageHeader
        title="AI suggestions log"
        subtitle="Every AI suggestion shown to you and what you decided about it. Pillar 10 receipt."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          select size="small" label="Decision" sx={{ minWidth: 200 }}
          value={filter}
          onChange={(e) => setFilter(e.target.value as AIHumanAction | 'all')}
        >
          {ACTIONS.map((a) => (
            <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <DataState query={q} empty={{
        title: 'No AI events yet',
        description: 'When you use any AI capability (scribe, coding suggestions, triage), the events appear here.',
      }}>
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No events match this filter.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((e) => <Row key={e.id} event={e} />)}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function Row({ event }: { event: AIAssistanceEvent }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <SmartToyIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {event.capability_code}
              </Typography>
              <Chip size="small" variant="outlined" label={event.model_name} />
              <ActionBadge action={event.human_action} />
            </Stack>
            <Typography variant="caption" color="text.secondary"
                        className="tabular">
              {formatDateTime(event.created_at)}
              {event.appointment && ` · appointment ${String(event.appointment).slice(0, 8)}`}
              {event.confidence && ` · conf ${formatConfidence(event.confidence)}`}
              {event.latency_ms != null && ` · ${event.latency_ms} ms`}
            </Typography>
            {event.override_reason && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}>
                Reason: {event.override_reason}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ActionBadge({ action }: { action: AIHumanAction }) {
  const config: Record<AIHumanAction, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
    pending:   { label: 'pending', color: 'warning' },
    accepted:  { label: 'accepted', color: 'success' },
    edited:    { label: 'edited', color: 'success' },
    rejected:  { label: 'rejected', color: 'default' },
    escalated: { label: 'escalated', color: 'error' },
  };
  const c = config[action];
  return <Chip size="small" label={c.label} color={c.color} variant="outlined" />;
}

function formatConfidence(c: string | null): string {
  if (!c) return '—';
  const n = parseFloat(c);
  if (Number.isNaN(n)) return '—';
  return `${Math.round(n * 100)}%`;
}
